import React, { useState, useEffect, useRef } from 'react';
import { Upload, Check, Trash2, X, Download } from 'lucide-react';
import { corporateCardUploadApi, CorporateCardUpload } from '../services/corporateCardUploadApi';
import { useAlert } from '../contexts/AlertContext';
import { useAuth } from '../contexts/AuthContext';

const CorporateCardUploadPage: React.FC = () => {
  const { showToast, showConfirm } = useAlert();
  const { userRole } = useAuth();
  const isAdminOrDev = userRole === 'ADMIN' || userRole === 'DEVELOPER';
  const [uploads, setUploads] = useState<CorporateCardUpload[]>([]);
  const [selectedUpload, setSelectedUpload] = useState<CorporateCardUpload | null>(null);
  const [loading, setLoading] = useState(false);

  // 업로드 모달
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 현재 날짜 기준으로 업로드 가능한 년/월 계산
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentDay = now.getDate();

  // 관리자/개발자용 연/월 선택
  const [selectedYear, setSelectedYear] = useState(currentMonth === 1 ? currentYear - 1 : currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth === 1 ? 12 : currentMonth - 1);

  // 업로드 가능한 기간 계산
  // - 1일~10일: 전월만 등록 가능 (마감일)
  // - 11일 이후: 당월만 등록 가능
  const getUploadablePeriod = () => {
    if (currentDay <= 10) {
      // 전월만 등록 가능
      let prevMonth = currentMonth - 1;
      let prevYear = currentYear;
      if (prevMonth === 0) {
        prevMonth = 12;
        prevYear = currentYear - 1;
      }
      return {
        year: prevYear,
        month: prevMonth,
        label: `${prevYear}년 ${prevMonth}월`,
      };
    } else {
      // 당월만 등록 가능
      return {
        year: currentYear,
        month: currentMonth,
        label: `${currentYear}년 ${currentMonth}월`,
      };
    }
  };

  const uploadablePeriod = getUploadablePeriod();

  // 실제 업로드할 년/월 (관리자/개발자는 선택한 값, 일반 직원은 업로드 시점 기준 자동 계산)
  const uploadYear = isAdminOrDev ? selectedYear : uploadablePeriod.year;
  const uploadMonth = isAdminOrDev ? selectedMonth : uploadablePeriod.month;

  useEffect(() => {
    loadUploads();
  }, []);

  const loadUploads = async () => {
    try {
      setLoading(true);
      const data = await corporateCardUploadApi.getMyUploads();
      setUploads(data);
      if (data.length > 0 && !selectedUpload) {
        setSelectedUpload(data[0]);
      }
    } catch (err: any) {
      showToast(err.message || '데이터를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const result = await corporateCardUploadApi.upload(file, uploadYear, uploadMonth);
      showToast(result.message, 'success');
      setShowUploadModal(false);

      // 목록 새로고침
      const data = await corporateCardUploadApi.getMyUploads();
      setUploads(data);

      // multi-month 응답이면 첫 번째 항목 선택, 아니면 업로드된 항목 선택
      if (result.multiMonth) {
        if (data.length > 0) {
          setSelectedUpload(data[0]);
        }
      } else if (result.upload) {
        setSelectedUpload(result.upload);
      }
    } catch (err: any) {
      showToast(err.message || '업로드에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirm = async () => {
    if (!selectedUpload) return;
    const confirmed = await showConfirm('확정하시겠습니까?');
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await corporateCardUploadApi.confirm(selectedUpload.id);
      showToast(result.message, 'success');
      await loadUploads();
      setSelectedUpload(result.upload);
    } catch (err: any) {
      showToast(err.message || '확정에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnconfirm = async () => {
    if (!selectedUpload) return;
    const confirmed = await showConfirm('확정을 취소하시겠습니까?');
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await corporateCardUploadApi.unconfirm(selectedUpload.id);
      showToast(result.message, 'success');
      await loadUploads();
      setSelectedUpload(result.upload);
    } catch (err: any) {
      showToast(err.message || '확정 취소에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUpload) return;
    const confirmed = await showConfirm('삭제하시겠습니까?');
    if (!confirmed) return;

    try {
      setLoading(true);
      await corporateCardUploadApi.delete(selectedUpload.id);
      showToast('삭제되었습니다.', 'success');
      setSelectedUpload(null);
      // 삭제 후 목록 새로고침하고 첫 번째 항목 선택
      const data = await corporateCardUploadApi.getMyUploads();
      setUploads(data);
      if (data.length > 0) {
        setSelectedUpload(data[0]);
      }
    } catch (err: any) {
      showToast(err.message || '삭제에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      await corporateCardUploadApi.downloadTemplate();
      showToast('양식 다운로드가 완료되었습니다.', 'success');
    } catch (err: any) {
      showToast(err.message || '양식 다운로드에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('ko-KR') + '원';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const getStatusBadge = (status: string) => {
    if (status === 'CONFIRMED') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">확정</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">임시저장</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* 헤더 */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">법인카드 사용내역</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAdminOrDev
              ? '사용내역을 업로드하고 확정해주세요.'
              : `현재는 ${uploadablePeriod.label} 데이터만 등록 가능합니다. (마감: ${currentDay <= 10 ? `${currentYear}년 ${currentMonth}월 10일` : `${currentMonth === 12 ? currentYear + 1 : currentYear}년 ${currentMonth === 12 ? 1 : currentMonth + 1}월 10일`})`
            }
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Download size={18} />
            양식 다운로드
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <Upload size={18} />
            사용내역 등록
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* 월별 목록 */}
        <div className="col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">업로드 내역</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {uploads.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  업로드된 내역이 없습니다.
                </div>
              ) : (
                uploads.map((upload) => (
                  <button
                    key={upload.id}
                    onClick={() => setSelectedUpload(upload)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedUpload?.id === upload.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900">
                        {upload.year}년 {upload.month}월
                      </span>
                      {getStatusBadge(upload.status)}
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      {upload.itemCount}건 · {formatAmount(Number(upload.totalAmount))}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* 상세 내역 */}
        <div className="col-span-9">
          {selectedUpload ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* 상세 헤더 */}
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedUpload.year}년 {selectedUpload.month}월 사용내역
                  </h2>
                  <div className="mt-1 flex items-center gap-4">
                    {getStatusBadge(selectedUpload.status)}
                    <span className="text-sm text-gray-500">
                      {selectedUpload.itemCount}건 · 총 {formatAmount(Number(selectedUpload.totalAmount))}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {selectedUpload.status === 'DRAFT' && (
                    <>
                      <button
                        onClick={handleConfirm}
                        disabled={selectedUpload.itemCount === 0}
                        className={`flex items-center gap-1 px-4 py-2 text-sm rounded-lg ${
                          selectedUpload.itemCount > 0
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <Check size={16} />
                        확정
                      </button>
                      <button
                        onClick={handleDelete}
                        className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                        삭제
                      </button>
                    </>
                  )}
                  {selectedUpload.status === 'CONFIRMED' && (
                    <button
                      onClick={handleUnconfirm}
                      className="flex items-center gap-1 px-4 py-2 text-sm text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50"
                    >
                      <X size={16} />
                      확정 취소
                    </button>
                  )}
                </div>
              </div>

              {/* 항목 테이블 */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">일자</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">사용처</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">금액</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">적요</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">비고</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedUpload.items.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          등록된 항목이 없습니다. 엑셀 파일을 업로드해주세요.
                        </td>
                      </tr>
                    ) : (
                      selectedUpload.items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{formatDate(item.usageDate)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{item.merchant}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatAmount(Number(item.amount))}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{item.description || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{item.memo || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
              좌측에서 월을 선택하거나 새로운 사용내역을 등록해주세요.
            </div>
          )}
        </div>
      </div>

      {/* 업로드 모달 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">사용내역 등록</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">귀속 년월</label>
                {isAdminOrDev ? (
                  <div className="flex gap-2">
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                        <option key={y} value={y}>{y}년</option>
                      ))}
                    </select>
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(Number(e.target.value))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                        <option key={m} value={m}>{m}월</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 font-medium">
                    {uploadablePeriod.label}
                  </div>
                )}
                {!isAdminOrDev && (
                  <p className="mt-1 text-xs text-gray-500">
                    {currentDay <= 10
                      ? `마감: ${currentYear}년 ${currentMonth}월 10일까지`
                      : `마감: ${currentMonth === 12 ? currentYear + 1 : currentYear}년 ${currentMonth === 12 ? 1 : currentMonth + 1}월 10일까지`
                    }
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">엑셀 파일 업로드</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    엑셀 파일에는 일자, 사용처, 금액 컬럼이 필요합니다.
                  </p>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="text-xs text-indigo-600 hover:text-indigo-800 underline flex items-center gap-1"
                  >
                    <Download size={12} />
                    양식 다운로드
                  </button>
                </div>
              </div>

            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowUploadModal(false)}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 로딩 오버레이 */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
            <span>처리 중...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateCardUploadPage;
